import { useState, useEffect, Fragment } from "react";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import {useTheme} from "@material-ui/core";

import { selectUserInfo } from "../userInfo/userInfoSlice";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  fetchAddressAction,
  selectAutoComplete,
  setAutoCompleteValue,
} from "../address/addressSlice";

import { useTranslation, Trans } from "react-i18next";

import {AppTheme} from "../..";

import WarrantyThresholdBlock from "./WarrentyThresholdBlock";
import MapSearchResultBlock from "../search/MapSearchResultBlock";
import BusinessSearchResultBlock from "../search/BusinessSearchResultBlock";
import SatelliteSearchResultBlock from "../search/SatelliteSearchResultBlock";
import StreetViewSearchResultBlock from "../search/StreetViewSearchResultBlock";
import WeatheObservationsResultBlock from "./WeatherObservationsResultBlock";
import ClimateRiskResultBlock from "../search/ClimateRiskResultBlock";
import CatnatRiskListBlock from "../search/CatnatRiskListBlock";
import PerilRiskListBlock from "../search/PerilRiskListBlock";
import SiteSevesoListBlock from "../search/SiteSevesoListBlock";
import EventListBlock from "../search/EventListBlock";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import Link from "@mui/material/Link";


import AppBar from "../address/AppBar";

import PlacesAutocomplete from "../search/PlacesAutocomplete";

import Icon from "../../components/Icon";

import { BlockItemType } from "../../types";

import "../address/Address.css";
import "../search/Search.css";

// quick fix
import "../../components/NavigationMenu/index.css";

const drawerWidth = 350;

const render = (status: Status) => {
  return <h1>{status}</h1>;
};

const API_KEY = "AIzaSyDq2gssPa5aiQDHXNsQU7fP9DZd-v5pzho";

export const blockItems: BlockItemType[] = [
  {
    title: "Seuils Garanties",
    id: "warraty_threshold",
    iconType: "warrantyThreshold",
    accessRight: '/features/suravenir/admin'
  },
  {
    title: "blockTitle.weatherObservations",
    id: "weather_observations",
    iconType: "calendar",
    accessRight: "/features/weather_observations",
  },
  {
    title: "blockTitle.general",
    id: "map_search",
    iconType: "mapInfo",
  },
  {
    title: "blockTitle.activities",
    id: "business_search",
    iconType: "postalActivity",
  },
  {
    title: "blockTitle.aerial",
    id: "satellite_image_search",
    iconType: "paperPlane",
  },
  {
    title: "blockTitle.street",
    id: "streetview_image_search",
    iconType: "streetview",
  },
  // {
  //   title: "blockTitle.weather",
  //   id: "weather_search",
  //   iconType: "calendar",
  // },
  {
    title: "blockTitle.climateChange",
    id: "climate_risk",
    iconType: "climateData",
  },
  {
    title: "blockTitle.catNat",
    id: "catnat_risk",
    iconType: "catnatData",
  },
  {
    title: "blockTitle.mandates",
    id: "peril_risk",
    iconType: "perilData",
  },
  {
    title: "blockTitle.event",
    id: "event_risk",
    iconType: "perilData",
  },
  {
    title: "blockTitle.seveso",
    id: "site_seveso",
    iconType: "site_seveso",
  },
];

export default function ClippedDrawer() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const userInfo = useAppSelector(selectUserInfo);

  /* google place search management */
  const googleAutocomplete = useAppSelector(selectAutoComplete);

  useEffect(() => {
    (async () => {
      if (!googleAutocomplete) {
        return;
      }
      dispatch(fetchAddressAction(googleAutocomplete.value.place_id));
    })();
  }, [googleAutocomplete, dispatch]);

  const handleChange = (place: any) => {
    dispatch(setAutoCompleteValue(place));
  };

  /* end of google place search management */

  const [itemClicked/*, setItemClicked*/] = useState<string | null>(null);
  const theme = useTheme<AppTheme>();
  const iconContainerStyle = {
      flex: 1,
      justifyContent: "center",
      height: "2.5rem",
      margin: ".3rem 0",
  };

  const dragIconContainerStyle = {
      flex: 1,
      justifyContent: "center",
      cursor: "move",
  };

  const style = {
      backgroundColor: theme.backgroundColor.primary,
  };

  const isAuthorized = userInfo.value && userInfo.value.groups && userInfo.value.groups.includes("/features/suravenir");
  const isSuravenirAdmin = userInfo.value && userInfo.value.groups && userInfo.value.groups.includes("/features/suravenir/admin");

  return (
    <Wrapper apiKey={API_KEY} libraries={["places", "marker"]} render={render}>
      <Box sx={{ display: "flex", scrollPaddingTop: "50px" }}>
        <CssBaseline />
        <AppBar
          title={t("applicationMenu.menuItem.suravenir")}
          position="fixed"
          sx={{ zIndex: (theme: any) => theme.zIndex.drawer + 1 }}
        />
        { !isAuthorized &&
          <Box
            sx={{
              paddingLeft: 3,
              paddingRight: 3,
              paddingTop: 10,
              textAlign: "center",
              flex: 1,
            }}
          >
            <Trans i18nKey="accessDeniedLabel">You don't have access to this feature, go back to <Link href="/">&nbsp;Home</Link></Trans>
          </Box>
        }
        {isAuthorized &&
          <Fragment>
             <Drawer
                variant="permanent"
                sx={{
                  width: drawerWidth,
                  flexShrink: 0,
                  [`& .MuiDrawer-paper`]: {
                    width: drawerWidth,
                    boxSizing: "border-box",
                  },
                }}
              >
                <Box
                  sx={{ paddingLeft: "12px", paddingRight: "12px", paddingTop: 10 }}
                >
                  <PlacesAutocomplete
                    initialValue={googleAutocomplete?.label}
                    onChange={handleChange}
                  />
                </Box>
                <Box>

                </Box>

                <Box style={{ flex: 1 }}>
                  <ul>
                    {blockItems.map(function(item, index) {
                      if (item.accessRight && userInfo.value && !userInfo.value.groups.includes(item.accessRight)) {
                        return null;
                      }
                      return (
                        <Fragment>
                          <li style={style} className={`NavItem${itemClicked === blockItems[index].id ? " Active" : ""}`}>
                            <a className="NavItemLink" href={`#${blockItems[index].id}`}>
                              <Icon type={blockItems[index].iconType} containerStyle={iconContainerStyle} />
                              <p className="NavItemLabel">{t(blockItems[index].title)}</p>
                              <Icon type="drag" iconStyle={{color: theme.color.white}} containerStyle={dragIconContainerStyle} />
                            </a>
                          </li>
                          { index === 0 && <li>
                            <p style={{ paddingLeft: "12px", paddingRight: "12px" }}>
                              Choisissez une région ou un code postal pour visulaliser le seuil météo
                            </p>
                          </li> }
                        </Fragment>
                      )
                    })}
                  </ul>
                </Box>

                <div
                  className="BuildNumber"
                  style={{ padding: "10px", textAlign: "center" }}
                >
                  build: {process.env.REACT_APP_BUILD_NUMBER}
                </div>
              </Drawer>
              <Box
                component="main"
                sx={{
                  flexGrow: 1,
                  paddingLeft: 3,
                  paddingRight: 3,
                  paddingTop: 10,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {isSuravenirAdmin && <WarrantyThresholdBlock {...blockItems[0]} />}
                <WeatheObservationsResultBlock {...blockItems[1]} />
                <MapSearchResultBlock {...blockItems[2]} />
                <BusinessSearchResultBlock {...blockItems[3]} />
                <SatelliteSearchResultBlock {...blockItems[4]} />
                <StreetViewSearchResultBlock {...blockItems[6]} />
                {/* <WeatherSearchResultBlock {...blockItems[index]} /> */}
                <ClimateRiskResultBlock {...blockItems[6]} />
                <CatnatRiskListBlock {...blockItems[7]} />
                <PerilRiskListBlock {...blockItems[8]}/>
                <EventListBlock {...blockItems[9]}/>
                <SiteSevesoListBlock {...blockItems[10]} />
              </Box>
          </Fragment>
        }

      </Box>
    </Wrapper>
  );
}
